import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { invalidateAfterConsultationMutation } from 'core/api/invalidation';
import { queryKeys } from 'core/api/queryKeys';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { AssignedSpecialistCard } from 'features/consultations/AssignedSpecialistCard';
import { consultationFocusRefetch, publicConsultationView, shouldPresentAssignedSpecialist } from 'features/consultations/consultationUtils';
import { userApi } from 'features/home/home.api';
import { useConfirm } from 'shared/components/ConfirmDialog';
import { Button } from 'shared/components/Button';
import { Card, ErrorState, Skeleton, StatusChip } from 'shared/components/Feedback';
import { PageMeta } from 'shared/components/PageMeta';
import { PatternChip } from 'shared/components/PatternBar';
import { useToast } from 'shared/components/Toast';
import { formatDateTimeInTimeZone, safeMeetingUrl } from 'shared/utilities/format';

export function ConsultationDetailPage() {
  const { id } = useParams();
  const { t, locale } = useI18n();
  const confirm = useConfirm();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const query = useQuery({
    queryKey: queryKeys.consultation(id),
    queryFn: () => userApi.consultation(id),
    refetchOnWindowFocus: (query) => consultationFocusRefetch(query?.state?.data?.status),
    refetchOnReconnect: (query) => consultationFocusRefetch(query?.state?.data?.status),
    staleTime: 0,
  });

  async function cancel() {
    if (busy) {
      return;
    }
    await confirm({
      title: t('consultations.cancelTitle'),
      body: t('consultations.cancelBody'),
      action: async () => {
        setBusy(true);
        try {
          await userApi.cancelConsultation(id);
          await invalidateAfterConsultationMutation(queryClient, id);
        } catch (err) {
          toast.error(humanizeApiError(err, t));
        } finally {
          setBusy(false);
        }
      },
    });
  }

  async function withdraw() {
    if (busy) {
      return;
    }
    await confirm({
      title: t('consultations.withdrawTitle'),
      body: t('consultations.withdrawBody'),
      action: async () => {
        setBusy(true);
        try {
          await userApi.withdrawConsent(id);
          await invalidateAfterConsultationMutation(queryClient, id);
        } catch (err) {
          toast.error(humanizeApiError(err, t));
        } finally {
          setBusy(false);
        }
      },
    });
  }

  if (query.isLoading) {
    return <Skeleton rows={7} />;
  }
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={() => query.refetch()} retryLabel={t('actions.retry')} />;
  }

  const item = publicConsultationView(query.data);
  const meetingUrl = safeMeetingUrl(item.meeting_url, item.status);
  const scheduled = formatDateTimeInTimeZone(item.scheduled_at, locale, item.display_timezone);
  const reason = item.cancellation_reason || item.rejection_reason;

  return (
    <div className="detail-page detail-layout">
      <PageMeta titleKey="meta.consultations" />
      <Card>
        <div className="history-card-head">
          <h1>{item.reference}</h1>
          <StatusChip tone={item.status}>{item.status_label || t(`consultations.statuses.${item.status}`)}</StatusChip>
        </div>
        <dl className="detail-list">
          <div><dt>{t('consultations.goal')}</dt><dd>{item.goal_label || item.goal}</dd></div>
          <div><dt>{t('consultations.period')}</dt><dd>{item.preferred_period_label || item.preferred_period}</dd></div>
          <div><dt>{t('consultations.method')}</dt><dd>{item.communication_method_label || item.communication_method}</dd></div>
          <div><dt>{t('consultations.question')}</dt><dd>{item.question}</dd></div>
          {item.scheduled_at ? (
            <div>
              <dt>{t('consultations.scheduled')}</dt>
              <dd>
                {scheduled.datetime}
                {scheduled.timeZoneLabel ? ` (${scheduled.timeZoneLabel})` : ''}
              </dd>
            </div>
          ) : null}
          {reason ? (
            <div><dt>{t('consultations.reason')}</dt><dd>{reason}</dd></div>
          ) : null}
        </dl>
        {meetingUrl ? (
          <a className="btn btn-secondary" href={meetingUrl} target="_blank" rel="noopener noreferrer" data-testid="consultation-meeting">
            {t('consultations.meeting')}
          </a>
        ) : null}
        {(item.top_patterns || item.result?.patterns || item.patterns || []).length ? (
          <div className="chip-row">
            {(item.top_patterns || item.result?.patterns || item.patterns || []).map((pattern) => (
              <PatternChip key={pattern.id || pattern.short_code} pattern={pattern} locale={locale} />
            ))}
          </div>
        ) : null}
        <div className="action-row">
          {item.status === 'pending_review' ? <Button tone="danger" onClick={cancel} loading={busy} disabled={busy}>{t('actions.cancel')}</Button> : null}
          {item.share_report_consent && !item.consent_withdrawn_at ? (
            <Button tone="ghost" onClick={withdraw} loading={busy} disabled={busy}>{t('actions.withdraw')}</Button>
          ) : null}
          {item.attempt_id ? <Link className="text-link" to={paths.result(item.attempt_id)}>{t('actions.viewResult')}</Link> : null}
        </div>
      </Card>
      {shouldPresentAssignedSpecialist(item) ? (
        <Card>
          <h2>{t('consultations.specialist')}</h2>
          <AssignedSpecialistCard consultation={item} headingLevel="h3" />
        </Card>
      ) : null}
    </div>
  );
}
